/**
 * PATTERN GRAMMAR — the composable bullet vocabulary.
 * Spec: bible 07-WAVE-ORCHESTRATOR §3.
 *
 * This is the language enemy fire is written in. Waves are authored offline as
 * grammar terms, validated for survivability, and executed here deterministically.
 * No LLM at runtime — this file is the "fast, dumb, deterministic interpreter"
 * the bible calls for.
 *
 * THE LAW THAT GOVERNS EVERY TERM: the gap always exists. Each emitter below
 * declares where its survivable path is, in world units, and the validator can
 * check that declaration against the player's hitbox (0.22 u) and cruise speed.
 * A pattern with no declared gap does not ship.
 *
 * On making it beautiful rather than merely dense — the four devices used here:
 *   1. SYMMETRY, DELIBERATELY BROKEN. A ring with one member missing reads as
 *      composed rather than random, and the missing member IS the invitation.
 *   2. NEGATIVE SPACE AS THE SUBJECT. The player does not look at bullets; they
 *      look at the hole. Patterns are authored hole-first.
 *   3. PHRASING. Emitters fire on musical subdivisions of the stage BPM, so
 *      volleys land with the score instead of against it.
 *   4. MOTIF AND VARIATION. A stage repeats one shape at escalating parameters,
 *      so the player learns a sentence and then hears it conjugated.
 */

import { ENEMY_BULLET_SPEED_CAP } from '../game/combat'

/** Emitter kinds. The grammar is closed — authored waves may use only these. */
export type PatternKind =
  | 'aimed_burst'
  | 'ring'
  | 'fan'
  | 'wall_with_gap'
  | 'spiral'
  | 'converging_lanes'
  | 'petal'
  /** Swept beam. Boss-only — telegraphed by a hairline for its full charge. */
  | 'beam'

export interface PatternSpec {
  kind: PatternKind
  /** Projectiles per volley. */
  count: number
  /** World units/sec. Hard-capped at 26 — see bible 05-BESTIARY §0.4. */
  speed: number
  /** Total arc in degrees (fan, ring, petal). */
  spreadDeg?: number
  /** Aim at the player, or fire in a fixed world direction. */
  aimed?: boolean
  /** Fixed direction in degrees when not aimed. 90 = straight down. */
  angleDeg?: number
  /** Index of the omitted member — the authored gap. */
  gapIndex?: number
  /** How many adjacent members the gap spans. */
  gapWidth?: number
  /** Degrees the emitter advances per volley (spiral). */
  rotationPerVolley?: number
  /** Volleys per second. */
  cadenceHz: number
  /** Milliseconds of visible wind-up before the first projectile. */
  telegraphMs: number
  /** Projectile visual type — drives colour and shape. */
  bullet: 'pellet' | 'bolt' | 'blob' | 'dart'
  /** Beam only: degrees the beam sweeps across its duration. */
  sweepDeg?: number
}

export interface EmittedBullet {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  kind: PatternSpec['bullet']
}

const DEG = Math.PI / 180

/** Rendered radii, from bible 05-BESTIARY §0.4. */
export const BULLET_RADIUS: Record<PatternSpec['bullet'], number> = {
  pellet: 0.4,
  bolt: 0.32,
  blob: 0.62,
  dart: 0.22,
}

/**
 * Expand one volley into concrete projectiles.
 *
 * `phase` advances per volley so spirals and petals rotate. Everything is a
 * pure function of (spec, origin, target, phase) — same inputs, same bullets,
 * every run. That determinism is what lets the offline validator prove a wave
 * survivable and trust the runtime to reproduce it.
 */
export function emit(
  spec: PatternSpec,
  ox: number,
  oy: number,
  targetX: number,
  targetY: number,
  phase: number,
): EmittedBullet[] {
  const out: EmittedBullet[] = []
  const speed = Math.min(spec.speed, ENEMY_BULLET_SPEED_CAP)
  const radius = BULLET_RADIUS[spec.bullet]

  const baseAngle = spec.aimed
    ? Math.atan2(targetY - oy, targetX - ox)
    : (spec.angleDeg ?? 270) * DEG

  const push = (angle: number, sx = ox, sy = oy, mult = 1) => {
    out.push({
      x: sx,
      y: sy,
      vx: Math.cos(angle) * speed * mult,
      vy: Math.sin(angle) * speed * mult,
      radius,
      kind: spec.bullet,
    })
  }

  const inGap = (i: number) => {
    if (spec.gapIndex === undefined) return false
    const w = spec.gapWidth ?? 1
    return i >= spec.gapIndex && i < spec.gapIndex + w
  }

  switch (spec.kind) {
    /** Straight at the player. Forces movement; the gap is "not where you are". */
    case 'aimed_burst': {
      const spread = (spec.spreadDeg ?? 0) * DEG
      for (let i = 0; i < spec.count; i++) {
        const t = spec.count === 1 ? 0 : i / (spec.count - 1) - 0.5
        push(baseAngle + t * spread)
      }
      break
    }

    /**
     * Full circle. Static, so it tells you where to move rather than chasing.
     * With gapIndex set it becomes a composed shape rather than a wall.
     */
    case 'ring': {
      for (let i = 0; i < spec.count; i++) {
        if (inGap(i)) continue
        push(baseAngle + (i / spec.count) * Math.PI * 2 + phase)
      }
      break
    }

    /** Arc segment. The workhorse — cheap, readable, endlessly conjugable. */
    case 'fan': {
      const spread = (spec.spreadDeg ?? 60) * DEG
      for (let i = 0; i < spec.count; i++) {
        if (inGap(i)) continue
        const t = spec.count === 1 ? 0 : i / (spec.count - 1) - 0.5
        push(baseAngle + t * spread)
      }
      break
    }

    /**
     * A horizontal line of bullets with an authored hole. The purest expression
     * of gap-reading: the whole pattern is a question with one answer.
     */
    case 'wall_with_gap': {
      const span = spec.spreadDeg ?? 30 // reused as world-unit width here
      for (let i = 0; i < spec.count; i++) {
        if (inGap(i)) continue
        const t = i / (spec.count - 1) - 0.5
        push(baseAngle, ox + t * span, oy)
      }
      break
    }

    /**
     * Rotating arms. Reads as motion rather than as a wall, and the corridors
     * between arms sweep — so the safe place is always moving predictably.
     */
    case 'spiral': {
      const arms = spec.count
      for (let i = 0; i < arms; i++) {
        push(baseAngle + (i / arms) * Math.PI * 2 + phase * (spec.rotationPerVolley ?? 12) * DEG)
      }
      break
    }

    /**
     * Two streams angled inward that cross ahead of the player. The corridor
     * closes over time, which converts a spatial problem into a timing one.
     */
    case 'converging_lanes': {
      const spread = (spec.spreadDeg ?? 24) * DEG
      const half = Math.floor(spec.count / 2)
      for (let i = 0; i < half; i++) {
        const off = (i + 1) * 0.9
        push(baseAngle + spread, ox - off, oy)
        push(baseAngle - spread, ox + off, oy)
      }
      break
    }

    /**
     * BEAM. Rendered as a dense line of fast projectiles along the sweep angle,
     * which keeps it inside the existing bullet pipeline — no special-case
     * collision, and the validator can still prove a gap.
     *
     * The gap is TEMPORAL rather than spatial: the beam occupies one angle at
     * a time, so the survivable path is "be somewhere it is not sweeping".
     */
    case 'beam': {
      const sweep = ((spec.sweepDeg ?? 90) * DEG)
      const angle = baseAngle - sweep / 2 + sweep * ((phase % 1))
      for (let i = 0; i < spec.count; i++) {
        const dist = 1.4 + i * 1.5
        push(angle, ox + Math.cos(angle) * dist, oy + Math.sin(angle) * dist)
      }
      break
    }

    /**
     * Overlapping offset rings that bloom outward like a flower. Pure
     * spectacle, and the most "art" of the set — but it still declares a gap,
     * because a beautiful pattern that cannot be survived is just a cutscene.
     */
    case 'petal': {
      const petals = spec.count
      const spread = (spec.spreadDeg ?? 18) * DEG
      for (let i = 0; i < petals; i++) {
        if (inGap(i)) continue
        const a = baseAngle + (i / petals) * Math.PI * 2 + phase
        push(a)
        push(a + spread, ox, oy, 0.82)
        push(a - spread, ox, oy, 0.82)
      }
      break
    }
  }

  return out
}

/**
 * The declared survivable path, in world units.
 *
 * The offline validator (bible 07 §4) uses this to prove a wave is clearable
 * before it enters the build. A returned width below 2.5 x the player hitbox
 * (0.22 u) fails validation.
 */
export function declaredGapWidth(spec: PatternSpec): number {
  const w = spec.gapWidth ?? 1
  switch (spec.kind) {
    case 'wall_with_gap':
      return ((spec.spreadDeg ?? 30) / spec.count) * w
    case 'ring':
    case 'petal':
      // Arc length of the omitted sector at a nominal 6 u engagement radius.
      return (w / spec.count) * 2 * Math.PI * 6
    case 'fan':
      return spec.gapIndex === undefined
        ? Infinity // an open fan is escaped around its edge
        : ((spec.spreadDeg ?? 60) / spec.count) * w * DEG * 6
    case 'aimed_burst':
      return Infinity // survivable by lateral movement, not by a hole
    case 'spiral':
      return ((360 / spec.count) * DEG * 6) / 2
    case 'converging_lanes':
      return Math.max(0, 6 - (spec.spreadDeg ?? 24) * DEG * 6)
    case 'beam':
      // A sweeping beam is escaped by being off its current angle. The
      // effective corridor is everything the sweep is not currently covering.
      return (360 - (spec.sweepDeg ?? 90)) * DEG * 6
  }
}
